import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Search, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';

interface ModelNameProps {
  modelName?: string;
  availableModels?: string[];
  isLoading?: boolean;
  onModelChange?: (model: string) => void;
  className?: string;
}

const ModelName = memo(({
  modelName = "MODEL_NAME",
  availableModels = [],
  isLoading = false,
  onModelChange,
  className
}: ModelNameProps) => {
  const hasModels = availableModels.length > 0;

  // Parse model display string to extract components including logo and provider
  const parseModelString = (modelStr: string) => {
    // Check if string contains logo delimiter ||
    const parts = modelStr.split('||');

    // New OpenRouter format: displayName||company_name||logoUrl||full_model_id||(details)
    if (parts.length === 5) {
      const details = parts[4].replace(/^\(|\)$/g, '').trim();
      const provider = parts[1].trim(); // Company name for grouping
      return {
        id: parts[0].trim(),  // Display name
        logo: parts[2].trim(), // Logo URL
        details: details,     // Just cost and context info
        provider: provider
      };
    }

    // OpenRouter format without logo: displayName||company_name||full_model_id||(details)
    if (parts.length === 4) {
      const details = parts[3].replace(/^\(|\)$/g, '').trim();
      const provider = parts[1].trim(); // Company name for grouping
      return {
        id: parts[0].trim(),  // Display name
        logo: '',
        details: details,
        provider: provider
      };
    }

    // Legacy format: displayName||provider||logoUrl||(details)
    if (parts.length === 4 && !parts[3].includes('(')) {
      const details = parts[3].replace(/^\(|\)$/g, '').trim();
      const provider = parts[1].trim();
      return {
        id: parts[0].trim(),  // Display name or model ID
        logo: parts[2].trim(), // Logo URL
        details: details,     // Just cost and context info
        provider: provider
      };
    }

    // Format without logo: displayName||provider||(details)
    if (parts.length === 3) {
      const details = parts[2].replace(/^\(|\)$/g, '').trim();
      const provider = parts[1].trim();
      return {
        id: parts[0].trim(),
        logo: '',
        details: details,
        provider: provider
      };
    }

    // Fallback to old format without logo or provider
    const match = modelStr.match(/^([^\(]+)\s*\((.+)\)$/);
    if (match) {
      const details = match[2];
      return {
        id: match[1].trim(),
        logo: '',
        details: details,
        provider: ''
      };
    }
    return { id: modelStr, logo: '', details: '', provider: '' };
  };

  const currentModel = parseModelString(modelName);

  // Group models by provider dynamically from JSON data
  const groupModelsByProvider = () => {
    // Use a Map to preserve insertion order (JSON order)
    const groupsMap = new Map<string, string[]>();
    const providerOrder: string[] = [];

    availableModels.forEach((model) => {
      const parsed = parseModelString(model);
      // Use provider from details string (e.g., "local", "openai", "Qwen", "Anthropic")
      const groupName = parsed.provider || 'Other';

      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, []);
        providerOrder.push(groupName);
      }
      groupsMap.get(groupName)!.push(model);
    });

    // Convert Map to array of entries, preserving JSON order
    return providerOrder.map(provider => [provider, groupsMap.get(provider)!] as [string, string[]]);
  };

  const groupedModels = groupModelsByProvider();

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!menuOpen) {
      setSearchTerm('');
    } else {
      const frame = window.requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
      return () => window.cancelAnimationFrame(frame);
    }
    return undefined;
  }, [menuOpen]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredGroupedModels = useMemo(() => {
    if (!normalizedSearch) return groupedModels;
    return groupedModels
      .map(([provider, models]) => {
        const kept = models.filter((model) =>
          parseModelString(model).id.toLowerCase().includes(normalizedSearch)
        );
        return [provider, kept] as [string, string[]];
      })
      .filter(([, kept]) => kept.length > 0);
  }, [normalizedSearch, groupedModels]);

  return (
    <div className={cn("flex items-center", className)}>
      {isLoading ? (
        <div className={cn(
          'flex items-center gap-1.5 px-2 py-1 min-w-[180px]',
          'bg-muted border border-border shadow-sm rounded-md',
          'transition-colors'
        )}>
          <div className="w-4 h-4 rounded bg-muted-foreground/30 animate-pulse flex-shrink-0" />
          <div className="flex flex-col items-start gap-0.5 flex-1 min-w-0">
            <span className="text-[11px] font-bold text-muted-foreground">
              Loading models...
            </span>
            <div className="h-2 w-16 bg-muted-foreground/30 rounded animate-pulse" />
          </div>
        </div>
      ) : hasModels ? (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <div className={cn(
              'flex items-center gap-1.5 px-2 py-1 cursor-pointer min-w-[180px]',
              'bg-card border border-border shadow-sm rounded-md',
              'hover:bg-accent hover:border-primary/40',
              'transition-colors'
            )}>
              {currentModel.logo && (
                <img
                  src={currentModel.logo}
                  alt=""
                  className="w-4 h-4 rounded object-contain flex-shrink-0"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <div className="flex flex-col items-start gap-0 flex-1 min-w-0">
                <div className="flex items-center justify-between w-full">
                  <span className="text-[11px] font-bold text-foreground truncate">
                    {currentModel.id}
                  </span>
                  <ChevronDown className="h-2.5 w-2.5 text-primary flex-shrink-0 ml-1" />
                </div>
                {currentModel.details && (
                  <span className="text-[9px] text-muted-foreground font-medium truncate w-full leading-tight">
                    {currentModel.details}
                  </span>
                )}
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[420px] max-h-[70vh] p-0 bg-popover text-popover-foreground border-border flex flex-col"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="sticky top-0 z-10 border-b border-border bg-popover p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search models..."
                  aria-label="Search models"
                  className={cn(
                    'w-full rounded-md border border-border bg-background px-7 py-1.5 text-xs text-foreground',
                    'placeholder:text-muted-foreground',
                    'focus:outline-none focus:ring-1 focus:ring-ring'
                  )}
                />
                {searchTerm && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearchTerm('');
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-1.5">
              {filteredGroupedModels.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No models match &ldquo;{searchTerm}&rdquo;
                </div>
              ) : (
                filteredGroupedModels.map(([provider, models], groupIndex) => (
                  <div key={provider}>
                    {groupIndex > 0 && <DropdownMenuSeparator className="my-1.5 bg-border" />}
                    <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {provider}
                    </DropdownMenuLabel>
                    {models.map((model) => {
                      const parsed = parseModelString(model);
                      const isSelected = model === modelName;
                      return (
                        <DropdownMenuItem
                          key={model}
                          onClick={() => onModelChange?.(model)}
                          className={cn(
                            'cursor-pointer p-2.5 rounded-md mx-1 my-0.5 transition-colors',
                            'hover:bg-accent hover:text-foreground',
                            isSelected && 'bg-primary/10 text-foreground border-l-2 border-primary'
                          )}
                        >
                          <div className="flex items-center gap-2.5 w-full">
                            {parsed.logo && (
                              <img
                                src={parsed.logo}
                                alt=""
                                className="w-5 h-5 rounded object-contain flex-shrink-0"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                              <span className="font-semibold text-sm text-foreground truncate">
                                {parsed.id}
                              </span>
                              {parsed.details && (
                                <span className="text-[11px] text-muted-foreground leading-tight">
                                  {parsed.details}
                                </span>
                              )}
                            </div>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className={cn(
          'flex items-center gap-1.5 px-2 py-1 min-w-[180px]',
          'bg-card border border-border shadow-sm rounded-md'
        )}>
          {currentModel.logo && (
            <img
              src={currentModel.logo}
              alt=""
              className="w-4 h-4 rounded object-contain flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="flex flex-col items-start gap-0 flex-1 min-w-0">
            <span className="text-[11px] font-bold text-foreground truncate">
              {currentModel.id}
            </span>
            {currentModel.details && (
              <span className="text-[9px] text-muted-foreground font-medium truncate w-full leading-tight">
                {currentModel.details}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

ModelName.displayName = 'ModelName';

export { ModelName };
